import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import {
  SecuritiesAndStocksDetailsType,
  SecuritiesAndStocksGeneralType,
} from '../../sj-orm/models/assets/categories';
import {
  ClassOfSharesType,
  ManagerialRightsType,
  SecuritiesAndStocksEnum,
  StrategyType,
  TradeType,
} from '../../sj-orm/models/assets/enums';
import { ContactDto, ContactType } from '../../sj-orm/models/contact.dto';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type SecuritiesAndStocks = SecuritiesAndStocksFormGeneralType &
  SecuritiesAndStocksFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const SecuritiesAndStocksList = [
  {
    label: 'forms_assets:stocks.enums.type.adr',
    key: SecuritiesAndStocksEnum.adr,
  },
  {
    label: 'forms_assets:stocks.enums.type.edr',
    key: SecuritiesAndStocksEnum.edr,
  },
  {
    label: 'forms_assets:stocks.enums.type.gdr',
    key: SecuritiesAndStocksEnum.gdr,
  },
  {
    label: 'forms_assets:stocks.enums.type.depositaryReceipts',
    key: SecuritiesAndStocksEnum.depositaryReceipts,
  },
  {
    label: 'forms_assets:stocks.enums.type.preferredStock',
    key: SecuritiesAndStocksEnum.preferredStock,
  },
  {
    label: 'forms_assets:stocks.enums.type.regular',
    key: SecuritiesAndStocksEnum.regular,
  },
];

const TradeTypesList = [
  { label: 'forms_assets:stocks.enums.tradeType.etc', key: TradeType.etc },
  { label: 'forms_assets:stocks.enums.tradeType.otc', key: TradeType.otc },
];

const ManagerialRightsTypesList = [
  {
    label: 'forms_assets:stocks.enums.managementRights.yes',
    key: ManagerialRightsType.yes,
  },
  {
    label: 'forms_assets:stocks.enums.managementRights.no',
    key: ManagerialRightsType.no,
  },
  {
    label: 'forms_assets:stocks.enums.managementRights.proportionally',
    key: ManagerialRightsType.proportionally,
  },
  {
    label: 'forms_assets:stocks.enums.managementRights.disproportionateUpward',
    key: ManagerialRightsType.disproportionateUpward,
  },
  {
    label: 'forms_assets:stocks.enums.managementRights.disproportionateDownward',
    key: ManagerialRightsType.disproportionateDownward,
  },
];

const ClassOfSharesTypesList = [
  { label: 'forms_assets:stocks.enums.class.A', key: ClassOfSharesType.A },
  { label: 'forms_assets:stocks.enums.class.B', key: ClassOfSharesType.B },
  { label: 'forms_assets:stocks.enums.class.C', key: ClassOfSharesType.C },
  { label: 'forms_assets:stocks.enums.class.other', key: ClassOfSharesType.OTHER },
];

const StrategyTypesList = [
  {
    label: 'forms_assets:stocks.enums.strategy.growthStock',
    key: StrategyType.growthStock,
  },
  {
    label: 'forms_assets:stocks.enums.strategy.valueStock',
    key: StrategyType.valueStock,
  },
  {
    label: 'forms_assets:stocks.enums.strategy.dividendStock',
    key: StrategyType.dividendStock,
  },
  {
    label: 'forms_assets:stocks.enums.strategy.other',
    key: StrategyType.other,
  },
];

type SecuritiesAndStocksHelpGeneralType = {
  nickname: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  type: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.ASSET_MARKET_VALUE_CALCULATOR; // TODO: add currency
  amount: QuestionType.NUMBER;
  holder: QuestionType.PICK_ARRAY_OF_DTO;
};

type SecuritiesAndStocksHelpDetailsType = {
  isin?: QuestionType.TEXT;
  ticker?: QuestionType.TEXT;
  issuer?: QuestionType.TEXT;
  acquisitionDate?: QuestionType.DATE_TIME;
  brokerageAccountNumber?: QuestionType.TEXT;
  depositaryName?: QuestionType.TEXT;
  depositaryAccountNumber?: QuestionType.TEXT;
  tradeType?: QuestionType.SELECT_FROM_LIST;
  tradingPlatformSeller?: QuestionType.TEXT;
  seller?: QuestionType.TEXT;
  tradingPlatform?: QuestionType.TEXT;
  managementRights?: QuestionType.SELECT_FROM_LIST;
  classOfShares?: QuestionType.SELECT_FROM_LIST;
  otherClassOfShares?: QuestionType.TEXT;
  powers?: QuestionType.PICK_ARRAY_OF_DTO;
  personalIdentifiers?: QuestionType.PICK_ARRAY_OF_DTO;
  assetInfoDocuments?: QuestionType.PICK_ARRAY_OF_DTO;
  assetInfoComments?: QuestionType.TEXT_MULTILINE;
  strategyType?: QuestionType.SELECT_FROM_LIST;
  strategyDescription?: QuestionType.TEXT_MULTILINE;
  attachments?: QuestionType.FILE_UPLOAD;
};

type SecuritiesAndStocksFormGeneralType = TDtoWrapper<
  SecuritiesAndStocksGeneralType,
  SecuritiesAndStocksHelpGeneralType
>;

type SecuritiesAndStocksFormDetailsType = TDtoWrapper<
  SecuritiesAndStocksDetailsType,
  SecuritiesAndStocksHelpDetailsType
>;

const SecuritiesAndStocksFormGeneral: SecuritiesAndStocksFormGeneralType = {
  nickname: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:stocks.inputs.type.label',
    tooltip: 'forms_assets:stocks.inputs.type.tooltip',
    value: SecuritiesAndStocksEnum.adr,
    items: SecuritiesAndStocksList,
    required: true,
  },
  amount: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:stocks.inputs.amount.label',
    value: 0,
    required: true,
  },
  purchaseValue: {
    type: QuestionType.ASSET_MARKET_VALUE_CALCULATOR,
    label: 'forms_assets:stocks.inputs.marketValue.label',
    tooltip: 'forms_assets:stocks.inputs.marketValue.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  holder: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:stocks.inputs.holder.label',
    colName: CollectionName.CONTACTS,
    value: [],
    tooltip: 'forms_assets:stocks.inputs.holder.tooltip',
    filter: (contact) =>
      [ContactType.BROKER, ContactType.BANK].includes((contact as unknown as ContactDto).type),
    dtoType: [ContactType.BANK, ContactType.BROKER],
  },
};

const SecuritiesAndStocksFormDetails: SecuritiesAndStocksFormDetailsType = {
  isin: {
    type: QuestionType.TEXT,
    label: 'forms_assets:stocks.inputs.isin.label',
    tooltip: 'forms_assets:stocks.inputs.isin.tooltip',
    order: 2,
  },
  ticker: {
    type: QuestionType.TEXT,
    label: 'forms_assets:stocks.inputs.ticker.label',
    tooltip: 'forms_assets:stocks.inputs.ticker.tooltip',
    order: 3,
  },
  issuer: {
    type: QuestionType.TEXT,
    label: 'forms_assets:stocks.inputs.issuer.label',
    tooltip: 'forms_assets:stocks.inputs.issuer.tooltip',
    order: 4,
  },
  acquisitionDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:stocks.inputs.acquisitionDate.label',
    order: 5,
  },
  brokerageAccountNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:stocks.inputs.brokerageAccountNumber.label',
    order: 6,
  },
  depositaryName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:stocks.inputs.depositaryName.label',
    order: 7,
  },
  depositaryAccountNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:stocks.inputs.depositaryAccountNumber.label',
    order: 8,
  },
  tradeType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:stocks.inputs.tradeType.label',
    items: TradeTypesList,
    order: 9,
  },
  tradingPlatformSeller: {
    type: QuestionType.TEXT,
    label: 'forms_assets:stocks.inputs.tradingPlatformSeller.label',
    order: 10,
  },
  seller: {
    type: QuestionType.TEXT,
    label: 'forms_assets:stocks.inputs.seller.label',
    showIf: {
      tradeType: {
        value: TradeType.otc,
      },
    },
    order: 11,
  },
  tradingPlatform: {
    type: QuestionType.TEXT,
    label: 'forms_assets:stocks.inputs.tradingPlatform.label',
    showIf: {
      tradeType: {
        value: TradeType.etc,
      },
    },
    order: 12,
  },
  managementRights: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:stocks.inputs.managementRights.label',
    tooltip: 'forms_assets:stocks.inputs.managementRights.tooltip',
    items: ManagerialRightsTypesList,
    order: 13,
  },
  classOfShares: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:stocks.inputs.classOfShares.label',
    tooltip: 'forms_assets:stocks.inputs.classOfShares.tooltip',
    items: ClassOfSharesTypesList,
    order: 14,
  },
  otherClassOfShares: {
    type: QuestionType.TEXT,
    label: 'forms_assets:stocks.inputs.otherClassOfShares.label',
    showIf: {
      classOfShares: { value: ClassOfSharesType.OTHER },
    },
    order: 15,
  },
  strategyType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:stocks.inputs.strategyType.label',
    items: StrategyTypesList,
    order: 16,
  },
  strategyDescription: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:stocks.inputs.strategyDescription.label',
    order: 17,
  },
  attachments: baseAssetFields.attachments,
};

const SecuritiesAndStocksForm = [
  SecuritiesAndStocksFormGeneral,
  SecuritiesAndStocksFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'specificsOfTaxation',
    'profitability',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'taxAgent',
    'taxAgentOther',
  ]).form,
  assetsDocumentsBuilder(),
];

export { SecuritiesAndStocksForm };
